import React, { useEffect, useState } from 'react';

import ArticleGrid, { TEMPLATE_TYPE } from '@/components/article-grid/article-grid';
import { getRelatedArticles } from '@/helpers/BuilderHelper';
import { useLocale } from '@/helpers/LangHelper';
import { useAsync } from '@/hooks/useAsync';
import { Article } from '@/types/Article';

import styles from './related-articles.module.scss';

export interface RelatedArticlesProps {
  offset?: number;
  article: Article;
}

const LIMIT = 3;

export const RelatedArticles = ({ offset: offsetFromProps = 0, article }: RelatedArticlesProps) => {
  const { locale } = useLocale();
  const [articles, setArticles] = useState<Article[]>([]);
  const [offset, setOffsetArticle] = useState<number>(offsetFromProps);
  const [isLoadingMoreArticles, setIsLoadingMoreArticles] = useState<boolean>(true);

  const loadMoreArticles = async () => {
    return await getRelatedArticles(article, locale);
  };

  const getUniqueArticles = (newArticles: Article[]) => {
    const mapper: { [id: string]: Article } = {};

    newArticles.forEach((article) => {
      mapper[article.builderModel.id] = article;
    });

    return Object.values(mapper);
  };

  const asyncObject = useAsync({
    asyncFunc: loadMoreArticles,
    funcParams: {},
    immediate: true,
  });

  useEffect(() => {
    if (!asyncObject.data) {
      asyncObject.execute();
      return;
    }

    let newArticles: Article[] = [];
    if (asyncObject.data) {
      newArticles = getUniqueArticles(asyncObject.data);
    }

    setArticles(newArticles);
  }, [asyncObject.data]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingMoreArticles(false);

    if (offset !== offsetFromProps || offset === -1) {
      setIsLoadingMoreArticles(true);

      const loadData = async () => {
        try {
          const newArticles = await getRelatedArticles(article, locale);
          if (!cancelled && newArticles) {
            setArticles((oldArticles) => getUniqueArticles([...oldArticles, ...newArticles]));
          }
        } catch (e) {
          console.error(e);
        } finally {
          if (!cancelled) {
            setIsLoadingMoreArticles(false);
          }
        }
      };

      loadData();
    }

    return () => {
      cancelled = true;
    };
  }, [offset]);

  useEffect(() => {
    let cancelled = false;

    setIsLoadingMoreArticles(true);
    const loadData = async () => {
      try {
        const newArticles = await getRelatedArticles(article, locale);
        if (!cancelled && newArticles) {
          setArticles(() => getUniqueArticles(newArticles));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setIsLoadingMoreArticles(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [article]);

  if (!articles?.length) return null;

  return (
    <div>
      <div className={styles.title}>Related Posts</div>
      <ArticleGrid
        articles={articles}
        isLoadingMoreArticles={isLoadingMoreArticles}
        setOffsetArticle={setOffsetArticle}
        offsetArticle={offset}
        templateType={TEMPLATE_TYPE.ONE_LINE}
        itemPerPage={LIMIT}
        showLoadMore={false}
      />
    </div>
  );
};
